import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  GlobalStyles,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Clear,
  Print,
  RestaurantMenu,
  Search,
} from '@mui/icons-material';
import {
  Formik,
  FormikErrors,
  FormikHelpers,
  FormikTouched,
  type FormikProps,
} from 'formik';
import { mixed, object, string } from 'yup';
import { deleteField } from 'firebase/firestore';
import {
  LABEL_RELEVANT_GROUP_FIELDS,
  clearLabelUrlsForGroup,
  createGroup,
  updateGroup,
  useActor,
} from '../lib/audited-writes';
import { useFirebaseContext } from '../lib/firebase-context';
import { useToast } from '../lib/toast-context';
import { Attendee, Grade, Group, Rotation } from '../lib/types';
import {
  GRADES,
  GROUP_NAME_MAX_LENGTH,
  TEACHERS_MAX_LENGTH,
} from '../lib/constants';
import { formatGroupShort, sortGroups } from '../lib/groups';
import ManageGroups from '../components/groups/manage-groups';
import GroupsRoster, {
  PRINT_TARGET_ID,
} from '../components/groups/groups-roster';
import RotationsView from '../components/groups/rotations-view';
import AttendeeForm from '../components/attendee-form';
import { useDebouncedValue } from '../lib/hooks/use-debounced-value';

type GroupFormValues = {
  grade: Grade | '';
  groupName: string;
  teachers: string;
  rotation: string; // rotation ref, '' = none
};

const blankFormValues: GroupFormValues = {
  grade: '',
  groupName: '',
  teachers: '',
  rotation: '',
};

// Builds a Yup schema for the group form. Uniqueness is enforced on the
// `${grade} | ${groupName}` short label.
const buildSchema = (existingGroups: Group[], excludeRef?: string) =>
  object()
    .shape({
      grade: mixed<Grade>()
        .oneOf(GRADES, 'Grade is required')
        .required('Grade is required'),
      groupName: string()
        .trim()
        .required('Group name is required')
        .max(
          GROUP_NAME_MAX_LENGTH,
          `Group name must be ${GROUP_NAME_MAX_LENGTH} characters or less`
        ),
      teachers: string()
        .trim()
        .required('Teachers is required')
        .max(
          TEACHERS_MAX_LENGTH,
          `Teachers must be ${TEACHERS_MAX_LENGTH} characters or less`
        ),
    })
    .test(
      'unique-grade-name',
      'A group with this grade and name already exists',
      function (values) {
        if (!values.grade || !values.groupName) return true;
        const candidateLabel =
          `${values.grade} | ${values.groupName.trim()}`.toLowerCase();
        const conflict = existingGroups.find(
          (g) =>
            g.ref !== excludeRef &&
            formatGroupShort(g).toLowerCase() === candidateLabel
        );
        if (!conflict) return true;
        return this.createError({
          path: 'groupName',
          message: 'A group with this grade and name already exists',
        });
      }
    );

const GroupFormFields = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  isSubmitting,
  rotations,
}: {
  values: GroupFormValues;
  errors: FormikErrors<GroupFormValues>;
  touched: FormikTouched<GroupFormValues>;
  handleChange: FormikProps<GroupFormValues>['handleChange'];
  handleBlur: FormikProps<GroupFormValues>['handleBlur'];
  isSubmitting: boolean;
  rotations: Rotation[];
}) => (
  <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      <FormControl
        error={touched.grade && Boolean(errors.grade)}
        disabled={isSubmitting}
        sx={{ minWidth: 120 }}
      >
        <InputLabel id='group-grade-label'>Grade</InputLabel>
        <Select
          labelId='group-grade-label'
          name='grade'
          value={values.grade}
          label='Grade'
          onChange={handleChange}
          onBlur={handleBlur}
        >
          {GRADES.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </Select>
        {touched.grade && errors.grade && (
          <FormHelperText>{errors.grade}</FormHelperText>
        )}
      </FormControl>
      <TextField
        name='groupName'
        label='Group name'
        value={values.groupName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.groupName && Boolean(errors.groupName)}
        helperText={
          (touched.groupName && errors.groupName) ||
          `${values.groupName.length}/${GROUP_NAME_MAX_LENGTH}`
        }
        inputProps={{ maxLength: GROUP_NAME_MAX_LENGTH }}
        disabled={isSubmitting}
        sx={{ flex: 1 }}
      />
    </Stack>
    <TextField
      name='teachers'
      label='Teachers'
      value={values.teachers}
      onChange={handleChange}
      onBlur={handleBlur}
      error={touched.teachers && Boolean(errors.teachers)}
      helperText={
        (touched.teachers && errors.teachers) ||
        `${values.teachers.length}/${TEACHERS_MAX_LENGTH}`
      }
      inputProps={{ maxLength: TEACHERS_MAX_LENGTH }}
      disabled={isSubmitting}
      fullWidth
    />
    <FormControl disabled={isSubmitting} fullWidth>
      <InputLabel id='group-rotation-label'>Rotation</InputLabel>
      <Select
        labelId='group-rotation-label'
        name='rotation'
        value={values.rotation}
        label='Rotation'
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <MenuItem value=''>
          <em>— None —</em>
        </MenuItem>
        {[...rotations]
          .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
          )
          .map((r) => (
            <MenuItem key={r.ref} value={r.ref}>
              {r.name}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  </Stack>
);

// Single dialog used for both Add (group=null) and Edit (group=Group) modes.
const GroupFormDialog = ({
  open,
  group,
  groups,
  rotations,
  attendees,
  activeEventRef,
  onClose,
  onSaved,
  onError,
  onLabelClearWarning,
}: {
  open: boolean;
  group: Group | null;
  groups: Group[];
  rotations: Rotation[];
  attendees: Attendee[];
  activeEventRef: string | undefined;
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
  // Fired after a successful edit when the follow-up label-cache invalidation
  // failed entirely or partially. Separate from `onError` because the group
  // save itself succeeded — we just want to surface that some attendees may
  // still print with the old group label until they're re-edited or the
  // admin bulk-clears.
  onLabelClearWarning: (msg: string) => void;
}) => {
  const actor = useActor();
  const isEdit = group !== null;
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const initialValues = useMemo<GroupFormValues>(
    () =>
      group
        ? {
            grade: group.grade,
            groupName: group.groupName,
            teachers: group.teachers,
            rotation: group.rotationRef || '',
          }
        : blankFormValues,
    [group]
  );

  const validationSchema = useMemo(
    () => buildSchema(groups, group?.ref),
    [groups, group?.ref]
  );

  const handleSubmit = async (
    values: GroupFormValues,
    { setSubmitting }: FormikHelpers<GroupFormValues>
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const basePayload = {
        grade: values.grade as Grade,
        groupName: values.groupName.trim(),
        teachers: values.teachers.trim(),
      };
      if (group) {
        // Mid-session safety: refuse to apply edits if the admin flipped the
        // active event while this dialog was open — the group doc would no
        // longer belong to the event the user is viewing.
        if (!activeEventRef || group.eventRef !== activeEventRef) {
          onError('Active event changed — close and reopen this group');
          setSubmitting(false);
          setIsWorking(false);
          return;
        }
        // Edit mode: explicit rotationRef set, or deleteField() to remove.
        await updateGroup(actor, group, {
          ...basePayload,
          rotationRef: values.rotation
            ? values.rotation
            : deleteField(),
        });
        // The printed badge shows group fields, so if any label-relevant
        // field changed, every attendee in this group has a stale cached
        // label. Clear them so the next print regenerates from current
        // data. Done after the group write so a failure here doesn't
        // block the rename itself — we surface a warning instead and the
        // admin can re-trigger via the bulk-clear button.
        const labelDirty = LABEL_RELEVANT_GROUP_FIELDS.some(
          (f) => basePayload[f] !== group[f],
        );
        if (labelDirty) {
          try {
            const { cleared, failed } = await clearLabelUrlsForGroup(
              actor,
              group,
              attendees,
            );
            if (failed > 0 && cleared === 0) {
              onLabelClearWarning(
                'Group saved, but cached labels could not be cleared. Use Admin → Clear cached labels to retry.',
              );
            } else if (failed > 0) {
              onLabelClearWarning(
                `Group saved. Cleared ${cleared} cached label${cleared === 1 ? '' : 's'}, but ${failed} failed. Use Admin → Clear cached labels to retry.`,
              );
            }
          } catch (clearErr) {
            console.error(
              'Group saved but label-cache invalidation threw',
              clearErr,
            );
            onLabelClearWarning(
              'Group saved, but cached labels could not be cleared. Use Admin → Clear cached labels to retry.',
            );
          }
        }
      } else {
        if (!activeEventRef) {
          onError('No active event — set one in Admin first');
          setSubmitting(false);
          setIsWorking(false);
          return;
        }
        // Add mode: include rotationRef only when chosen, so absence is the
        // canonical "unassigned" shape (matches existing optional-field
        // semantics in the codebase).
        const addPayload: Record<string, unknown> = {
          ...basePayload,
          eventRef: activeEventRef,
        };
        if (values.rotation) {
          addPayload.rotationRef = values.rotation;
        }
        await createGroup(actor, addPayload);
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving group', error);
      onError(isEdit ? 'Failed to update group' : 'Failed to add group');
    }
    setSubmitting(false);
    setIsWorking(false);
    if (succeeded) {
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>{isEdit ? 'Edit group' : 'Add group'}</DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <GroupFormFields
                values={values}
                errors={errors}
                touched={touched}
                handleChange={handleChange}
                handleBlur={handleBlur}
                isSubmitting={isSubmitting}
                rotations={rotations}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add group'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

type SubTab = 'groups' | 'roster' | 'rotations';

// Roster column-visibility toggles are persisted in localStorage so a
// page refresh keeps the admin's preference. Mirrors the `printerId`
// pattern in `check-in-date.tsx`. Values are stored as `'1'` / `'0'`
// strings (anything else is treated as false on read).
const SHOW_CHECK_IN_STATUS_KEY = 'rosterShowCheckInStatus';
const SHOW_GRADE_COMPLETED_KEY = 'rosterShowGradeCompleted';
// Emergency contact defaults to ON (it's a safety-critical column
// volunteers expect to see by default), so reads fall back to `true`
// when localStorage has no entry yet. The persisted value is still
// `'1'` / `'0'` to match the other roster toggles.
const SHOW_EMERGENCY_CONTACT_KEY = 'rosterShowEmergencyContact';

const readPersistedBool = (key: string): boolean =>
  localStorage.getItem(key) === '1';

const readPersistedBoolWithDefault = (
  key: string,
  defaultValue: boolean,
): boolean => {
  const v = localStorage.getItem(key);
  if (v === null) return defaultValue;
  return v === '1';
};

const writePersistedBool = (key: string, value: boolean): void => {
  localStorage.setItem(key, value ? '1' : '0');
};

// Print-mode visibility overrides keyed on data attributes. We need !important
// here because the display:none/block coming from the per-tab `sx` lives on
// the same element and would otherwise win when the print stylesheet runs.
const tabPrintStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '[data-print-hide="true"]': { display: 'none !important' },
        '[data-print-show="true"]': { display: 'block !important' },
      },
    }}
  />
);

const GroupsPage = () => {
  const { groups, rotations, attendees, activeCheckInDate, activeEvent } =
    useFirebaseContext();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState<SubTab>('roster');
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Group | null>(null);
  const [addingRotation, setAddingRotation] = useState(false);
  const [addingAttendee, setAddingAttendee] = useState(false);
  const [showCheckInStatus, setShowCheckInStatus] = useState(() =>
    readPersistedBool(SHOW_CHECK_IN_STATUS_KEY),
  );
  const [showGradeCompleted, setShowGradeCompleted] = useState(() =>
    readPersistedBool(SHOW_GRADE_COMPLETED_KEY),
  );
  const [showEmergencyContact, setShowEmergencyContact] = useState(() =>
    readPersistedBoolWithDefault(SHOW_EMERGENCY_CONTACT_KEY, true),
  );
  const [editingRotations, setEditingRotations] = useState(false);
  const [printingGroupRef, setPrintingGroupRef] = useState<string | null>(null);
  // Kitchen PDF: a separate print artifact from the standard rotations
  // PDF. Setting this true triggers React to render `RotationsView` in
  // kitchen mode; the effect below then calls `window.print()` and
  // resets the flag once the print dialog closes. Same pattern as
  // `printingGroupRef`.
  const [kitchenPrintMode, setKitchenPrintMode] = useState(false);
  // Roster-only name search. Mirrors the Check-in page's debounced
  // pattern (input stays responsive; the filter recomputes 300ms after
  // the last keystroke). Cleared automatically when the admin leaves
  // the Roster sub-tab so a stale filter can't silently shrink a
  // single-group print on the Groups sub-tab.
  const [rosterSearch, setRosterSearch] = useState('');
  const debouncedRosterSearch = useDebouncedValue(rosterSearch, 300);
  // Roster-only group filter. Same rationale as `rosterSearch` (Roster-
  // scoped, cleared on tab switch). When set, the Roster shows only the
  // matching group's card; the Unassigned card is hidden too. Treated
  // alongside search for the Print PDF / Auto-assign disabled state so
  // those actions never silently operate on a hidden pool.
  const [rosterGroupFilter, setRosterGroupFilter] = useState('');
  const sortedGroups = useMemo(() => sortGroups(groups), [groups]);
  const searchActive = rosterSearch.trim().length > 0;
  const groupFilterActive = rosterGroupFilter.length > 0;
  const rosterFilterActive = searchActive || groupFilterActive;
  // Roster-wide aggregate: total kids that need the lesson delivered
  // in Russian. Computed from the full unfiltered attendees pool so
  // the line stays stable while admins search/filter the cards. Hidden
  // when zero; the per-group breakdown line already surfaces local
  // counts implicitly via the RU chips in the Flags column.
  const russianCount = useMemo(
    () => attendees.filter((a) => a.needs_russian).length,
    [attendees],
  );

  // When a single-group print is requested, wait for React to apply the
  // override stylesheet, then trigger print. Reset on `afterprint` so
  // subsequent top-level "Print PDF" reverts to the full roster.
  useEffect(() => {
    if (!printingGroupRef) return;
    const reset = () => setPrintingGroupRef(null);
    window.addEventListener('afterprint', reset);
    const id = window.setTimeout(() => window.print(), 0);
    return () => {
      window.removeEventListener('afterprint', reset);
      window.clearTimeout(id);
    };
  }, [printingGroupRef]);

  // Kitchen PDF: same shape as the single-group print — let React paint
  // the kitchen variant first, then trigger print, then reset on the
  // browser's afterprint event so the screen reverts to the standard
  // rotations view.
  useEffect(() => {
    if (!kitchenPrintMode) return;
    const reset = () => setKitchenPrintMode(false);
    window.addEventListener('afterprint', reset);
    const id = window.setTimeout(() => window.print(), 0);
    return () => {
      window.removeEventListener('afterprint', reset);
      window.clearTimeout(id);
    };
  }, [kitchenPrintMode]);

  // Reset the search box and group filter when the admin leaves the
  // Roster sub-tab. Both are Roster-only concerns; persisting them
  // would let a hidden filter shrink a Groups-tab single-group print
  // to zero matches. Depend only on `activeTab` so this fires exactly
  // once per tab change (not on every keystroke / dropdown change).
  useEffect(() => {
    if (activeTab !== 'roster') {
      setRosterSearch('');
      setRosterGroupFilter('');
    }
  }, [activeTab]);

  // Defensive: clear the group filter if its target group disappears
  // (deleted from the Groups tab, removed by VBS sync, or the active
  // event flipped). Without this, MUI's Select renders blank for an
  // out-of-range value, logs a console warning, and the empty state
  // misleadingly says "No attendees in this group" for a group that
  // no longer exists. Stable groups reference between snapshots makes
  // this a no-op on the common path.
  useEffect(() => {
    if (
      rosterGroupFilter &&
      !groups.some((g) => g.ref === rosterGroupFilter)
    ) {
      setRosterGroupFilter('');
    }
  }, [groups, rosterGroupFilter]);

  // Print-mode override: hide every roster page except the one being printed,
  // and clear the trailing page-break so we don't get a blank trailing page.
  // Also force the Roster panel visible regardless of the active sub-tab,
  // since per-row Print is triggered from the Groups tab without switching.
  // Hide the Groups print target so its data-print-show='true' (when the
  // Groups tab is active) doesn't leak both panels into the same PDF.
  const singleGroupPrintStyles = printingGroupRef ? (
    <GlobalStyles
      styles={{
        '@media print': {
          '#roster-panel': { display: 'block !important' },
          '#groups-print-target': { display: 'none !important' },
          [`#${PRINT_TARGET_ID} > .roster-page`]: {
            display: 'none !important',
          },
          [`#${PRINT_TARGET_ID} > .roster-page[data-group-ref="${printingGroupRef}"]`]:
            {
              display: 'block !important',
              breakAfter: 'auto !important',
              pageBreakAfter: 'auto !important',
            },
        },
      }}
    />
  ) : null;

  return (
    <>
      <Box
        className='roster-print-page-header'
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
        }}
      >
        <Typography variant='h5'>Manage</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {activeTab === 'groups' && (
            <Tooltip title='Toggle inline rotation editing on the Groups list'>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={editingRotations}
                    onChange={(e) => setEditingRotations(e.target.checked)}
                  />
                }
                label={
                  <Typography variant='body2'>Edit rotations</Typography>
                }
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}
          {activeTab === 'roster' && (
            <Tooltip
              title={
                !activeCheckInDate
                  ? 'No active check-in date'
                  : 'Show check-in status column on the Roster (and in PDF)'
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={showCheckInStatus && Boolean(activeCheckInDate)}
                    onChange={(e) => {
                      setShowCheckInStatus(e.target.checked);
                      writePersistedBool(
                        SHOW_CHECK_IN_STATUS_KEY,
                        e.target.checked,
                      );
                    }}
                    disabled={!activeCheckInDate}
                  />
                }
                label={
                  <Typography variant='body2'>Check-in status</Typography>
                }
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}
          {activeTab === 'roster' && (
            <Tooltip title="Show 'Grade completed' (GC) column on the Roster (and in PDF)">
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={showGradeCompleted}
                    onChange={(e) => {
                      setShowGradeCompleted(e.target.checked);
                      writePersistedBool(
                        SHOW_GRADE_COMPLETED_KEY,
                        e.target.checked,
                      );
                    }}
                  />
                }
                label={
                  <Typography variant='body2'>Grade completed</Typography>
                }
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}
          {activeTab === 'roster' && (
            <Tooltip title="Show 'Emergency contact' (EC) column on the Roster (and in PDF)">
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={showEmergencyContact}
                    onChange={(e) => {
                      setShowEmergencyContact(e.target.checked);
                      writePersistedBool(
                        SHOW_EMERGENCY_CONTACT_KEY,
                        e.target.checked,
                      );
                    }}
                  />
                }
                label={
                  <Typography variant='body2'>Emergency contact</Typography>
                }
                sx={{ mr: 1 }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={
              rosterFilterActive && activeTab === 'roster'
                ? 'Clear filters to print'
                : ''
            }
            placement='bottom'
          >
            <span>
              <Button
                variant='outlined'
                startIcon={<Print />}
                onClick={() => window.print()}
                disabled={rosterFilterActive && activeTab === 'roster'}
              >
                Print PDF
              </Button>
            </span>
          </Tooltip>
          {activeTab === 'rotations' && (
            <Button
              variant='outlined'
              startIcon={<RestaurantMenu />}
              onClick={() => setKitchenPrintMode(true)}
              disabled={kitchenPrintMode}
            >
              Kitchen PDF
            </Button>
          )}
          {activeTab === 'rotations' && (
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={() => setAddingRotation(true)}
            >
              Add rotation
            </Button>
          )}
          {activeTab === 'groups' && (
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={() => setAdding(true)}
            >
              Add group
            </Button>
          )}
          {activeTab === 'roster' && (
            <Button
              variant='contained'
              startIcon={<Add />}
              onClick={() => setAddingAttendee(true)}
            >
              Add attendee
            </Button>
          )}
        </Box>
      </Box>

      <Tabs
        className='roster-print-subtabs'
        value={activeTab}
        onChange={(_e, value: SubTab) => setActiveTab(value)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label='Roster' value='roster' />
        <Tab label='Groups' value='groups' />
        <Tab label='Rotations' value='rotations' />
      </Tabs>

      {activeTab === 'roster' && (
        <Box
          className='roster-print-page-header'
          sx={{ display: 'flex', mb: 2, gap: 2, alignItems: 'center' }}
        >
          <TextField
            value={rosterSearch}
            onChange={(e) => setRosterSearch(e.target.value)}
            label='Search Roster'
            fullWidth
            size='small'
            variant='outlined'
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Search fontSize='small' />
                </InputAdornment>
              ),
              endAdornment: rosterSearch.length ? (
                <IconButton
                  size='small'
                  aria-label='Clear search'
                  onClick={() => setRosterSearch('')}
                >
                  <Clear fontSize='small' />
                </IconButton>
              ) : null,
            }}
          />
          <FormControl size='small' sx={{ minWidth: 220 }}>
            <InputLabel id='roster-filter-group-label'>Group</InputLabel>
            <Select
              labelId='roster-filter-group-label'
              value={rosterGroupFilter}
              label='Group'
              onChange={(e) => setRosterGroupFilter(e.target.value as string)}
            >
              <MenuItem value=''>
                <em>— All groups —</em>
              </MenuItem>
              {sortedGroups.map((group) => (
                <MenuItem key={group.ref} value={group.ref}>
                  {formatGroupShort(group)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {groupFilterActive && (
            <Tooltip title='Clear group filter' placement='top'>
              <IconButton
                size='small'
                aria-label='Clear group filter'
                onClick={() => setRosterGroupFilter('')}
              >
                <Clear fontSize='small' />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {tabPrintStyles}
      {singleGroupPrintStyles}
      {/*
        All three panels are always mounted so Print PDF can target whichever
        sub-tab is active. The data-print-* attributes flip with the active
        tab so only one panel is forced visible during print.
      */}
      <Box
        id='roster-panel'
        data-print-show={activeTab === 'roster' ? 'true' : undefined}
        data-print-hide={activeTab !== 'roster' ? 'true' : undefined}
        sx={{ display: activeTab === 'roster' ? 'block' : 'none' }}
      >
        <GroupsRoster
          showCheckInStatus={showCheckInStatus && Boolean(activeCheckInDate)}
          showGradeCompleted={showGradeCompleted}
          showEmergencyContact={showEmergencyContact}
          searchQuery={debouncedRosterSearch}
          groupFilterRef={rosterGroupFilter}
        />
        {russianCount > 0 && (
          <Box
            className='roster-print-page-header'
            sx={{ display: 'flex', mt: 1, mb: 2, alignItems: 'center' }}
          >
            <Typography variant='body2' color='text.secondary'>
              {russianCount === 1
                ? '1 attendee needs lesson in Russian'
                : `${russianCount} attendees need lesson in Russian`}
            </Typography>
          </Box>
        )}
      </Box>
      <Box
        data-print-show={activeTab === 'groups' ? 'true' : undefined}
        data-print-hide={activeTab !== 'groups' ? 'true' : undefined}
        sx={{ display: activeTab === 'groups' ? 'block' : 'none' }}
      >
        <ManageGroups
          editingRotations={editingRotations}
          onEditGroup={(group) => setEditing(group)}
          onPrintGroup={(group) => setPrintingGroupRef(group.ref)}
        />
      </Box>
      <Box
        data-print-show={activeTab === 'rotations' ? 'true' : undefined}
        data-print-hide={activeTab !== 'rotations' ? 'true' : undefined}
        sx={{ display: activeTab === 'rotations' ? 'block' : 'none' }}
      >
        <RotationsView
          adding={addingRotation}
          onCloseAdd={() => setAddingRotation(false)}
          kitchenMode={kitchenPrintMode}
        />
      </Box>

      <Dialog
        open={addingAttendee}
        onClose={() => setAddingAttendee(false)}
        fullWidth
      >
        <DialogTitle>Add attendee</DialogTitle>
        <DialogContent>
          {addingAttendee && (
            <AttendeeForm
              attendee={null}
              onClose={() => setAddingAttendee(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <GroupFormDialog
        open={adding || editing !== null}
        group={editing}
        groups={groups}
        rotations={rotations}
        attendees={attendees}
        activeEventRef={activeEvent?.ref}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(mode === 'add' ? 'Group added' : 'Group saved')
        }
        onError={(message) => toast.error(message)}
        onLabelClearWarning={(message) => toast.warning(message)}
      />
    </>
  );
};

export default GroupsPage;
